import type { GeneratedReport } from '../types/ai'

export const reports: GeneratedReport[] = [
  {
    id: 'rp-1',
    title: 'Bid Readiness Review — FIFA World Cup 2038 UAE Host Bid',
    type: 'bid-readiness',
    scope: 'SDU-MEG-2026-014',
    period: 'Q3 2026',
    audience: 'SDU Director & Federal Alignment Committee',
    author: 'AI Report Generator',
    createdOn: '2026-08-12',
    status: 'ready',
    executiveSummary:
      'The bid is at 84% requirement coverage against a gate threshold of 85%, driven by one outstanding mandatory guarantee and an accommodation shortfall in the northern cluster. All other technical volumes are complete and assured. On current trajectory the bid clears its gate 4 weeks before the FIFA submission window closes, provided the tax exemption guarantee is escalated in August.',
    findings: [
      { id: 'fi-1', label: 'One mandatory requirement outstanding', detail: 'The federal tax exemption guarantee is the sole mandatory requirement not evidenced. It is the binding constraint on gate clearance.' },
      { id: 'fi-2', label: 'Accommodation shortfall of 18,400 room nights', detail: 'Peak-demand modelling exceeds committed inventory in the northern cluster. Two competing bids score higher on this axis.' },
      { id: 'fi-3', label: 'Host city delivery capability uneven', detail: 'Three of five host emirates have no tournament-scale delivery record; an SDU delivery partner has been attached.' }
    ],
    scoredRows: [
      { subject: 'Host-body requirement coverage', score: 84, band: 'Manual review', note: '41 of 48 mandatory requirements evidenced' },
      { subject: 'Government guarantees issued', score: 72, band: 'Manual review', note: '7 of 10 letters signed' },
      { subject: 'Bid dossier completeness', score: 88, band: 'Advance', note: 'Volumes 1–4 issued, Volume 5 outstanding' },
      { subject: 'Competitive position', score: 76, band: 'Manual review', note: 'Behind Riyadh on accommodation' }
    ],
    recommendations: [
      'Escalate the federal tax exemption guarantee to the alignment committee in the August session.',
      'Commission a differentiation study on legacy and sustainability to offset the accommodation gap.',
      'Convert 9,000 of the shortfall into committed serviced-apartment inventory in Sharjah and Ajman.'
    ],
    appendix: [
      'Requirement matrix export — 48 mandatory items with evidence links',
      'Competing host city comparison — three declared bids',
      'Guarantee letter status by issuing authority'
    ]
  },
  {
    id: 'rp-2',
    title: 'Portfolio Status — Mega & Major Lifecycle, August 2026',
    type: 'portfolio-status',
    scope: 'All Mega & Major records',
    period: 'August 2026',
    audience: 'SDU Director',
    author: 'AI Report Generator',
    createdOn: '2026-08-15',
    status: 'ready',
    executiveSummary:
      'Eight Mega and Major records are live across the six stages. The portfolio is bottlenecked at Assessment, where two records are held on funding structure, and at Readiness, where the Asian Games 2034 gate is held on workforce readiness. Total committed support across approved records stands at AED 11.65B.',
    findings: [
      { id: 'fi-4', label: 'Assessment is the pipeline bottleneck', detail: 'Three records sit at Assessment with a mean 22 days in stage; two are held on co-funding below threshold.' },
      { id: 'fi-5', label: 'One gate held at Readiness', detail: 'Asian Games 2034 workforce domain is 11 points below threshold with 58 days to the gate review.' },
      { id: 'fi-6', label: 'Cost-to-complete pressure at Plan & Build', detail: 'COP38 has drawn 38% of mandate against 26% schedule completion.' }
    ],
    scoredRows: [
      { subject: 'World Expo 2035 — Dubai', score: 88.6, band: 'Advance', note: 'Approval stage, one sign-off outstanding' },
      { subject: 'Asian Games 2034 — Abu Dhabi', score: 87.9, band: 'Advance', note: 'Readiness gate held on workforce' },
      { subject: 'COP38 Climate Change Conference', score: 85.2, band: 'Advance', note: 'Cost-to-complete review recommended' },
      { subject: 'FIFA World Cup 2038 — UAE', score: 81.4, band: 'Advance', note: 'One mandatory guarantee outstanding' },
      { subject: 'Youth Olympic Games 2034 — UAE', score: 68.3, band: 'Manual review', note: 'Co-funding at 11%' }
    ],
    recommendations: [
      'Hold the Youth Olympic Games 2034 record at Assessment pending a revised funding structure.',
      'Second an SDU workforce specialist to Asian Games venue services ahead of the September rehearsal.',
      'Commission an independent cost-to-complete review on COP38 before the Plan & Build gate.'
    ],
    appendix: [
      'Stage distribution by track',
      'Days-in-stage distribution across the portfolio',
      'Gate status summary by record'
    ]
  },
  {
    id: 'rp-3',
    title: 'Outreach Performance — International Track, July 2026',
    type: 'outreach-performance',
    scope: 'All active outreach campaigns',
    period: 'July 2026',
    audience: 'Outreach & Ecosystem Team',
    author: 'AI Report Generator',
    createdOn: '2026-08-04',
    status: 'ready',
    executiveSummary:
      'Three campaigns are live across Silver, Gold and Platinum tiers, delivering 19.8M impressions against a combined guarantee of 27.2M with 46% of the windows remaining. Two campaigns have a single laggard channel each; both are recoverable through inventory reallocation without additional cost.',
    findings: [
      { id: 'fi-7', label: 'Embassy network under-delivering on Gold', detail: 'Abu Dhabi Festival embassy placements at 64% of target.' },
      { id: 'fi-8', label: 'Tourism board channel under-delivering on Silver', detail: 'Ajman Heritage Days tourism board placements at 58% of target.' },
      { id: 'fi-9', label: 'Overseas share exceeding tier assumptions', detail: 'Platinum campaign overseas share at 71% against a 60% assumption.' }
    ],
    scoredRows: [
      { subject: 'Dubai Esports & Games Festival (Platinum)', score: 108, band: 'Ahead of guarantee', note: '14.6M of 18M delivered' },
      { subject: 'Abu Dhabi Festival of Classical Arts (Gold)', score: 94, band: 'On track', note: '4.18M of 6.8M delivered' },
      { subject: 'Ajman Heritage Days (Silver)', score: 84, band: 'Behind', note: '1.06M of 2.4M delivered' }
    ],
    recommendations: [
      'Reallocate two national portal slots to cover the Ajman tourism board shortfall.',
      'Shift embassy network inventory on the Gold campaign into airline inflight placements.',
      'Review the Platinum tier overseas-share assumption ahead of the 2027 rate card.'
    ],
    appendix: [
      'Channel-level delivery against target',
      'Geographic distribution by campaign',
      'Weekly impression and engagement series'
    ]
  },
  {
    id: 'rp-4',
    title: 'Stage-Gate Review — COP38 Plan & Build',
    type: 'stage-gate-review',
    scope: 'SDU-MAJ-2025-041',
    period: 'Q3 2026',
    audience: 'Delivery Assurance Board',
    author: 'AI Report Generator',
    createdOn: '2026-07-30',
    status: 'ready',
    executiveSummary:
      'The Plan & Build gate cannot clear on current evidence. The procurement compliance certificate was rejected in July and one high risk remains open on cost-to-complete. Schedule completion of 26% against 38% budget draw-down is outside tolerance for a Major event of this envelope.',
    findings: [
      { id: 'fi-10', label: 'Procurement compliance certificate rejected', detail: 'Resubmission required before the gate can be assessed.' },
      { id: 'fi-11', label: 'Accreditation workstream 12% complete', detail: 'Tender award slipped six weeks, compressing the integration test window into Readiness.' }
    ],
    scoredRows: [
      { subject: 'Baseline schedule and budget approved', score: 100, band: 'Met', note: 'Approved 15 May 2026' },
      { subject: 'Procurement compliance certificate', score: 0, band: 'Outstanding', note: 'Rejected 29 July 2026' },
      { subject: 'No critical risks open', score: 50, band: 'Partial', note: 'One high risk open' }
    ],
    recommendations: [
      'Hold the gate and require resubmission of the procurement compliance certificate.',
      'Reassign the accreditation workstream to the SDU technology cell.',
      'Parallel-run the legacy accreditation platform as contingency through Readiness.'
    ],
    appendix: ['Workstream completion detail', 'Risk register extract', 'Milestone variance analysis']
  }
]
